/**
 * React Query mutation hook for deleting a time entry
 * Handles time entry deletion with automatic query invalidation
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteTimeEntry } from 'services/timerServices';
import { queryKeys } from '../../../config/queryClient';

/**
 * Hook to delete a time entry using React Query mutation
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.onSuccess - Callback function called on successful deletion
 * @param {Function} options.onError - Callback function called on deletion error
 *
 * @returns {Object} Mutation object
 * @returns {Function} returns.mutate - Function to trigger the mutation
 * @returns {Function} returns.mutateAsync - Async function to trigger the mutation
 * @returns {boolean} returns.isLoading - Loading state
 * @returns {boolean} returns.isError - Error state
 * @returns {Object|null} returns.error - Error object if mutation failed
 *
 * @example
 * const deleteTimeEntryMutation = useDeleteTimeEntryQuery({
 *   onSuccess: () => {
 *     console.log('Time entry deleted successfully');
 *   },
 *   onError: (error) => {
 *     console.error('Failed to delete time entry:', error);
 *   },
 * });
 *
 * // Usage
 * deleteTimeEntryMutation.mutate({
 *   time_entry_sk: 'TIME_ENTRY#123',
 *   is_lead: false,
 * });
 */
export const useDeleteTimeEntryQuery = ({ onSuccess, onError } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ time_entry_sk, is_lead = false }) => {
      // The service function expects the formatted SK and is_lead parameter
      return deleteTimeEntry(time_entry_sk, is_lead);
    },
    onSuccess: (data, variables, context) => {
      // Invalidate all time entry queries to refresh the list
      queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });

      // Call custom onSuccess callback if provided
      if (onSuccess) {
        onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      // Call custom onError callback if provided
      if (onError) {
        onError(error, variables, context);
      }
    },
  });
};

export default useDeleteTimeEntryQuery;
